import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DsBoxModule,
  DsButtonModule,
  DsIconModule,
  DsLabelModule,
  DsModalModule,
  DsToastModel,
  DsToastService,
} from '@bmw-ds/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { FileType } from 'src/app/shared/enums/file-type.enum';

import { BackofficeService } from '../../../core/services/backoffice.service';
import { BaseTone, ButtonTone, IconTextTone } from '../../../shared/enums/tone.enum';
import { MockedFile } from '../../../shared/models/backoffice/mocked-file.model';

@Component({
  selector: 'pure-delete-file-modal',
  imports: [DsBoxModule, TranslateModule, DsModalModule, DsIconModule, DsButtonModule, DsLabelModule],
  templateUrl: './delete-file.component.html',
  styleUrl: './delete-file.component.scss',
})
export class DeleteFileComponent {
  private readonly backofficeService = inject(BackofficeService);
  private readonly translationService = inject(TranslateService);
  private readonly toastService = inject(DsToastService);
  private readonly destroy = inject(DestroyRef);

  protected readonly BaseTone = BaseTone;
  protected readonly IconTextTone = IconTextTone;
  protected readonly ButtonTone = ButtonTone;

  vin = input.required<string>();
  file = input.required<MockedFile>();
  type = input.required<FileType>();
  closed = output<void>();
  fileDeleted = output<boolean>();

  modalIsOpen = signal<boolean>(true);

  protected readonly isDeletingFile = signal<boolean>(false);

  // TODO: this should be iin the store
  deleteFile(): void {
    this.isDeletingFile.set(true);
    this.backofficeService
      .deleteFile(this.vin(), this.file().id, this.type())
      .pipe(
        takeUntilDestroyed(this.destroy),
        finalize(() => this.isDeletingFile.set(false))
      )
      .subscribe({
        next: () => {
          const successMessage = this.translationService.instant('backoffice.deleteFile.success');
          this.sendToast('vin-deleting-success-toast', BaseTone.POSITIVE, successMessage);
          this.fileDeleted.emit(true);
        },
        error: (err: HttpErrorResponse) => {
          let errorMessage: string;
          if (err.status === 404) {
            errorMessage = this.translationService.instant('backoffice.deleteFile.error.notFound');
            this.sendToast('vin-deleting-error-toast', BaseTone.CAUTION, errorMessage);
          } else {
            errorMessage = this.translationService.instant('backoffice.deleteFile.error.generic');
            this.sendToast('vin-deleting-error-toast', BaseTone.CRITICAL, errorMessage);
          }
          this.fileDeleted.emit(false);
        },
      });
    this.modalDismissed();
  }

  modalDismissed(): void {
    this.modalIsOpen.set(false);
  }

  private sendToast(id: string, tone: BaseTone, toastText: string): void {
    this.toastService.pushToast({
      id,
      tone,
      toastText,
    } as DsToastModel);
  }
}
